import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Checkbox, IconCheck, Link, Modal,
  Skeleton,
} from 'gatorcomponents';
import withErrorBoundary from '@/utils/errorBoundary';
import snappyMonarxPath from '../../media/snappy-monarx.svg';
import * as Styles from './ProtectionModal.styles';
import { SERVICE_TERMS } from '@/config/urls/supportUrls';

const ProtectionModal = ({
  content,
  onButtonClick,
  onClose,
  testId,
  terms,
  loadingData = false,
  fastCheckoutLoading,
}) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <>
      <Styles.ModalCardOverride />
      <Modal
        maxWidth={800}
        maxHeight={662}
        noContentPadding
        noHeader
        onClose={onClose}
        testId={testId}
      >
        <Styles.Container>
          <Styles.Title>{content.title}</Styles.Title>
          <Styles.Subtitle>{loadingData ? <Skeleton variant="textLine" width="440px" /> : content.subtitle}</Styles.Subtitle>

          {content.message && (
            <Styles.Message>{content.message}</Styles.Message>
          )}

          <Styles.GradientCardWrapper>
            <Styles.GradientCard>
              <Styles.ProductHeader>
                <Styles.ProductTitleWrapper>
                  <Styles.ProductTitle>{content.productTitle}</Styles.ProductTitle>
                  {content.proBadgeLabel && (
                  <Styles.ProBadge>{content.proBadgeLabel}</Styles.ProBadge>
                  )}
                </Styles.ProductTitleWrapper>
                <Styles.ProductSubtitle>{content.productSubtitle}</Styles.ProductSubtitle>
              </Styles.ProductHeader>

              {(content.checklistItems || []).length > 0 && (
              <Styles.Checklist data-testid={`${testId}__checklist`}>
                {(content.checklistItems || []).map(item => (
                  <Styles.ChecklistItem key={item}>
                    <Styles.ChecklistIcon>
                      <IconCheck size="md" />
                    </Styles.ChecklistIcon>
                    {item}
                  </Styles.ChecklistItem>
                ))}
              </Styles.Checklist>
              )}

              <Styles.CheckboxWrapper onClick={() => setAgreed(!agreed)}>
                <Checkbox
                  checked={agreed}
                />

                <Styles.TermsLabel>
                  {terms.label}
                  <Link href={SERVICE_TERMS} text={terms.cta} />
                </Styles.TermsLabel>
              </Styles.CheckboxWrapper>

              <Styles.ButtonWrapper>
                {loadingData ? (
                  <Skeleton variant="button" />
                )
                  : (
                    <Button
                      label={content.buttonLabel}
                      onClick={onButtonClick}
                      size="xlarge"
                      variant="primary"
                      version="v2"
                      width="fit-content"
                      loading={fastCheckoutLoading}
                      disabled={!agreed || fastCheckoutLoading}
                    />
                  )
              }
              </Styles.ButtonWrapper>
            </Styles.GradientCard>

            <Styles.Mascot src={snappyMonarxPath} alt={content.mascotAlt} />
          </Styles.GradientCardWrapper>
        </Styles.Container>
      </Modal>
    </>
  );
};

ProtectionModal.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    message: PropTypes.string,
    checklistItems: PropTypes.arrayOf(PropTypes.string),
    buttonLabel: PropTypes.string.isRequired,
    productTitle: PropTypes.string.isRequired,
    productSubtitle: PropTypes.string.isRequired,
    proBadgeLabel: PropTypes.string,
    mascotAlt: PropTypes.string.isRequired,
  }).isRequired,
  terms: PropTypes.shape({
    label: PropTypes.string.isRequired,
    cta: PropTypes.string.isRequired,
  }).isRequired,
  onButtonClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  testId: PropTypes.string,
  loadingData: PropTypes.bool,
  fastCheckoutLoading: PropTypes.bool,
};

ProtectionModal.defaultProps = {
  testId: 'gator-protect-protection-modal',
};

export default withErrorBoundary(ProtectionModal, 'ProtectionModal');
